import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";

import api from "@helpers/api";

import CustomModal from "@components/CustomModal";

import { Button, Input, Text } from "@fragments";

import Parser from "@helpers/parser";
import {
    createStripeConnectLink,
    fetchWallets,
    WALLET_PROVIDERS,
} from "@helpers/wallets";

import './styles.scss'

import { Alert, Skeleton } from "@mui/material";
import { PixRounded } from "@mui/icons-material";


export default function RequestWithdrawalModal({ modalRef }) {
    const { t } = useTranslation("contentModals");
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState()

    const [loadingAccount, setLoadingAccount] = useState(true)
    const [account, setAccount] = useState(null)

    const REF_config = useRef({});
    const REF_callback = useRef();

    modalRef.current = {
        open(preFill, callback) {
            REF_config.current = { ...preFill };
            REF_callback.current = callback;
            setLoading(false)
            setError(null)
            if (preFill?.wallet) {
                setAccount({ ...preFill.wallet })
                setLoadingAccount(false)
            } else {
                setLoadingAccount(true)
                handleLoadAccount()
            }
            setOpen(true);
        },
    };

    useEffect(() => {
        if (loadingAccount) {
            handleLoadAccount()
        }
    }, [loadingAccount])


    async function handleSubmit() {
        try {
            if (isStripeAccount()) {
                await handleStripeDashboard()
                return
            }

            const requestAmount = parseInt(REF_config.current.amount) || 0;

            if (requestAmount < 500) {
                setError(t('requestWithdrawal.errors.minAmount'))
                return
            }

            if (requestAmount > account?.balance?.available) {
                setError(t('requestWithdrawal.errors.maxAmount', { value: Parser.price.brl(account?.balance?.available) }))
                return
            }

            setLoading(true);

            const { data } = await api.post("wallet/withdrawal", {
                provider: WALLET_PROVIDERS.ASAAS,
                amount: parseInt(REF_config.current.amount) || 0,
            });

            if (data?.result?.success) {
                await REF_callback.current(data);
                window.setSnackbar(t('requestWithdrawal.success'))
                setOpen(false);
            } else {
                if (data.error === 'amount_not_allowed') {
                    setError(t('requestWithdrawal.errors.minAmount'))
                } else if (data.result.fail_reason === 'nightly_limit_exceeded') {
                    setError(t('requestWithdrawal.errors.nightlyLimit'))
                } else {
                    if (data.result.fail_reason === 'incomplete_receiver_information') {
                        setError(t('requestWithdrawal.errors.incompleteReceiver'))
                    }
                }
            }
        } catch (e) {
            console.log(e);
        }

        setLoading(false);
    }

    async function handleLoadAccount() {
        const provider = REF_config.current.provider || WALLET_PROVIDERS.ASAAS
        const result = await fetchWallets();
        const nextAccount = provider === WALLET_PROVIDERS.STRIPE
            ? result.stripe
            : (result.asaas.raw || result.asaas)
        setAccount({ ...nextAccount })
        setLoadingAccount(false)
    }

    async function handleStripeDashboard() {
        setLoading(true)
        try {
            const link = await createStripeConnectLink({ type: 'dashboard' })
            if (REF_callback.current) await REF_callback.current(link)
            if (link?.url) {
                window.location.assign(link.url)
            }
        } catch (e) {
            console.log(e)
            setError(t('requestWithdrawal.errors.stripeOpen'))
        }
        setLoading(false)
    }

    async function handleConfigurePixKey() {
        setLoading(true)
        try {
            setError(null)

            const changing = !!REF_config.current.change
            const payload = {
                provider: WALLET_PROVIDERS.ASAAS,
                key: REF_config.current.pixKey,
                keyType: REF_config.current.keyType || 'email'
            }

            // Both routes sit behind the sensitive-action verification: the
            // axios interceptor runs the 428 flow and replays the call with the
            // grant, so a 2xx here already means "verified and saved".
            const response = changing
                ? await api.patch("wallet/withdrawal/account", payload)
                : await api.post("wallet/withdrawal/account", payload)

            // Backed out of (or locked out of) the identity check — not a key problem.
            if (response?.verificationCancelled) {
                setLoading(false)
                return
            }
            if (response?.verificationLocked) {
                setError(t('requestWithdrawal.errors.verificationLocked'))
                setLoading(false)
                return
            }

            const data = response?.data
            // PATCH answers the wallet resource (no `success` flag); POST keeps `{ success }`.
            const succeeded = changing
                ? response?.status >= 200 && response?.status < 300
                : !!data?.success

            if (succeeded) {
                if (changing) window.setSnackbar(t('requestWithdrawal.change.success'))

                if (REF_config.current.configureOnly) {
                    if (REF_callback.current) await REF_callback.current(data);
                    setOpen(false);
                } else {
                    setLoadingAccount(true)
                }
            } else {
                setError(t('requestWithdrawal.errors.invalidPixKey'))
            }
        } catch (e) {
            console.log(e)
            setError(t('requestWithdrawal.errors.configurePixKey'))
        }

        setLoading(false)
    }

    function AccountBox() {
        if (!account?.withdrawal?.key) return null

        const keyType = account?.withdrawal?.keyType || 'email'

        const label = t(`requestWithdrawal.keyTypes.${keyType}`)

        const keyFormatted = {
            email: Parser.email(account?.withdrawal?.key),
            phone: Parser.phone('+55' + account?.withdrawal?.key),
            cpf: Parser.cpf(account?.withdrawal?.key),
            cnpj: Parser.cnpj(account?.withdrawal?.key),
            random: account?.withdrawal?.key
        }[keyType] || account?.withdrawal?.key


        return (
            <div className="current-account-box bsf-8 mt-3">
                <h5 className="bold">{t('requestWithdrawal.pixKeyBoxTitle')}</h5>

                <small className="pix-key-row">
                    <PixRounded fontSize="small" />
                    <h5 className="bold" style={{ transform: 'translateY(2px)' }}>
                        <b className="pr-1">{label}:</b>
                        <small>{keyFormatted}</small>
                    </h5>
                </small>
            </div>
        )
    }

    function StripeDashboardPanel() {
        return (
            <div className="current-account-box bsf-8 mt-3">
                <Text.h5 weight={500} contrast={8}>Stripe Express</Text.h5>
                <Text.span contrast={6}>
                    {t('requestWithdrawal.stripe.description')}
                </Text.span>
                <Text.caption contrast={6} className="mt-1">
                    {t('requestWithdrawal.stripe.fees', { value: Parser.price.format(199, 'USD') })}
                </Text.caption>
            </div>
        )
    }

    function WithdrawalValueInput() {
        const [value, setValue] = useState(REF_config.current.amount || 0)

        useEffect(() => {
            REF_config.current.amount = value
        }, [value])

        function handleInputFullValue() {
            setValue(account?.balance?.available)
        }

        return (
            <div className="withdrawal-value-input">
                <Input.Money
                    className="mt-1 mb-1"
                    autoFocus={true}
                    value={value}
                    onInput={setValue}
                    label={t('requestWithdrawal.amountLabel')}
                    type="money"
                />

                <Button
                    className="mt-2"
                    color="overlay"
                    text={t('requestWithdrawal.fullAmountButton', { value: Parser.price.brl(account?.balance?.available || 0) })}
                    onClick={handleInputFullValue}
                />
            </div>
        )
    }

    function isStripeAccount() {
        return (REF_config.current.provider || account?.provider) === WALLET_PROVIDERS.STRIPE
    }

    const hasPixKeyConfigured = !!account?.withdrawal?.key && !!account?.withdrawal?.keyType
    const stripeAccount = isStripeAccount()
    // Opened from "Alterar chave": always the configure step, PATCHing the key.
    const changeMode = !!REF_config.current.change && !stripeAccount
    const showConfigure = changeMode || !hasPixKeyConfigured

    return (
        <CustomModal
            maxWidth={435}
            bottomButtonSumbitText={
                stripeAccount
                    ? t('requestWithdrawal.submitStripe')
                    : changeMode
                        ? t('requestWithdrawal.submitChange')
                        : (hasPixKeyConfigured ? t('requestWithdrawal.submitWithdraw') : t('requestWithdrawal.submitConfigure'))
            }
            bottomButtonCancelText={t('common:actions.cancel')}
            loading={loading || loadingAccount}
            title={
                stripeAccount
                    ? t('requestWithdrawal.stripeTitle')
                    : changeMode ? t('requestWithdrawal.change.modalTitle') : t('requestWithdrawal.title')
            }
            className="withdrawal-modal"
            handleSubmit={stripeAccount ? handleStripeDashboard : (showConfigure ? handleConfigurePixKey : handleSubmit)}
            open={open}
            handleClose={() => setOpen(false)}
            bodyComponent={
                open ? (
                    <div className="px-4 py-1">
                        {
                            loadingAccount ? <Skeleton variant="rectangular" height={100} /> : (
                                <>

                                    {
                                        stripeAccount ? <StripeDashboardPanel /> : !showConfigure ? <>
                                            <AccountBox />
                                            <h5 className="bold mt-3">{t('requestWithdrawal.amountSectionTitle')}</h5>
                                            <WithdrawalValueInput />
                                        </> : <ConfigurePixKey config={REF_config} changing={changeMode} />
                                    }
                                </>
                            )
                        }


                        {
                            error && <Alert className="mt-1 error-message" severity="error">{error}</Alert>
                        }
                    </div>
                ) : null
            }
        />
    );
}

// Module-level on purpose. Declared inside the modal's render body this got a
// new identity on every parent render (setLoading, setError), so React
// remounted it and the typed key was wiped — visible every time the
// verification modal was dismissed or the PATCH answered 422.
function ConfigurePixKey({ config, changing }) {
    const { t } = useTranslation("contentModals");
    const [pixKey, setPixKey] = useState(config.current.pixKey || '')
    const [keyType, setKeyType] = useState(config.current.keyType || 'email')

    useEffect(() => {
        config.current.pixKey = pixKey
    }, [config, pixKey])

    useEffect(() => {
        config.current.keyType = keyType
    }, [config, keyType])

    // `random` is core's PixKeyType for the EVP (random) key.
    const keyTypes = ['email', 'phone', 'cpf', 'cnpj', 'random'].map((value) => ({
        value,
        label: t(`requestWithdrawal.keyTypes.${value}`)
    }))

    function handleKeyType(value) {
        setKeyType(value)
        setPixKey('')
    }

    function renderPixKeyInput() {
        switch (keyType) {
            case 'phone':
                return (
                    <Input.Phone
                        className="mt-1"
                        label={t('requestWithdrawal.configure.phoneInput')}
                        value={pixKey}
                        onInput={setPixKey}
                    />
                )
            case 'cpf':
                return (
                    <Input.CPF
                        className="mt-1"
                        label={t('requestWithdrawal.configure.cpfInput')}
                        value={pixKey}
                        onInput={setPixKey}
                    />
                )
            case 'cnpj':
                return (
                    <Input.CNPJ
                        className="mt-1"
                        label={t('requestWithdrawal.configure.cnpjInput')}
                        value={pixKey}
                        onInput={setPixKey}
                    />
                )

            case 'email':
                return (
                    <Input.Email
                        className="mt-1"
                        label={t('requestWithdrawal.configure.emailInput')}
                        value={pixKey}
                        onInput={setPixKey}
                    />
                )
            case 'random':
            default:
                return (
                    <Input.Text
                        className="mt-1"
                        label={t('requestWithdrawal.configure.randomInput')}
                        value={pixKey}
                        onInput={setPixKey}
                    />
                )
        }
    }

    return (
        <div>
            <h4 className="bold mt-3">
                {changing ? t('requestWithdrawal.change.title') : t('requestWithdrawal.configure.title')}
            </h4>
            <h5>
                {changing ? t('requestWithdrawal.change.description') : t('requestWithdrawal.configure.description')}
            </h5>


            <h5 className="pt-2 bold">{t('requestWithdrawal.configure.keyTypeLabel')}</h5>
            <div className="mt-1 key-type-boxes">
                {
                    keyTypes.map(({ value, label }) => (
                        <Button
                            key={value}
                            color={keyType === value ? 'primary' : 'overlay'}
                            text={label}
                            size="small"
                            onClick={() => handleKeyType(value)}
                        />
                    ))
                }
            </div>

            <h5 className="pt-3 bold">{t('requestWithdrawal.configure.pixKeyLabel')}</h5>
            {renderPixKeyInput()}
        </div>
    )
}
